import React from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils'

export interface CalendarContextMenuItem {
  /** Stable id so React can key the row */
  id: string
  label: string
  icon?: React.ElementType
  /** When true, the item renders in destructive red. */
  danger?: boolean
  /** When true, render a divider above this item instead of a regular row. */
  separatorAbove?: boolean
  /** When true, the item is shown disabled. */
  disabled?: boolean
  onClick: () => void
}

interface CalendarContextMenuProps {
  open: boolean
  x: number
  y: number
  items: CalendarContextMenuItem[]
  onClose: () => void
}

const MENU_WIDTH = 220
const ESTIMATED_ROW_HEIGHT = 32

/**
 * Floating right-click menu used by the Bookings Calendar. Renders into a
 * portal so it can escape the calendar's overflow:hidden containers and
 * positions itself within the viewport (clamping near the screen edges).
 */
export default function CalendarContextMenu({ open, x, y, items, onClose }: CalendarContextMenuProps) {
  const ref = React.useRef<HTMLDivElement | null>(null)

  // Close on outside click or Escape. Mousedown rather than click avoids the
  // pop-and-close flicker when a right-click reopens the menu.
  React.useEffect(() => {
    if (!open) return
    const onMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    // Defer one tick so the event that opened the menu doesn't immediately close it.
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', onMouseDown)
      document.addEventListener('keydown', onKey)
    }, 0)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])

  if (!open) return null

  // Clamp position into the viewport so the menu never overflows. Right-click
  // near the right edge or near the bottom flips the menu to the other side.
  const vw = window.innerWidth
  const vh = window.innerHeight
  const estimatedHeight = items.length * ESTIMATED_ROW_HEIGHT + 8
  const left = Math.min(Math.max(8, x), vw - MENU_WIDTH - 8)
  const top = Math.min(Math.max(8, y), vh - estimatedHeight - 8)

  return createPortal(
    <div
      ref={ref}
      role="menu"
      // Stop scroll from feeling weird when opening; right-click also bubbles
      // up the contextmenu event so killing it here prevents the browser menu.
      onContextMenu={(e) => e.preventDefault()}
      className="fixed z-[70] py-1 rounded-lg border border-border/60 bg-popover text-popover-foreground shadow-2xl backdrop-blur-md animate-in fade-in-0 zoom-in-95 duration-100"
      style={{ left, top, width: MENU_WIDTH }}
    >
      {items.map((item) => {
        if (item.separatorAbove) {
          return (
            <React.Fragment key={item.id}>
              <div className="my-1 border-t border-border/50" />
              <ContextMenuRow item={item} onClose={onClose} />
            </React.Fragment>
          )
        }
        return <ContextMenuRow key={item.id} item={item} onClose={onClose} />
      })}
    </div>,
    document.body
  )
}

function ContextMenuRow({ item, onClose }: { item: CalendarContextMenuItem; onClose: () => void }) {
  const Icon = item.icon
  return (
    <button
      type="button"
      role="menuitem"
      disabled={item.disabled}
      onClick={() => {
        if (item.disabled) return
        item.onClick()
        onClose()
      }}
      className={cn(
        'w-full text-left flex items-center gap-2.5 px-3 py-1.5 text-xs transition-colors',
        'hover:bg-accent focus:bg-accent outline-none',
        item.danger && 'text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300',
        item.disabled && 'opacity-50 cursor-not-allowed hover:bg-transparent'
      )}
    >
      {Icon ? (
        <Icon className="h-3.5 w-3.5 shrink-0" />
      ) : (
        <span className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      )}
      <span className="truncate">{item.label}</span>
    </button>
  )
}

/**
 * Hook for wiring long-press → context menu on touch devices. Pass the same
 * onOpen handler that desktop right-click uses; coords come from the touch
 * point. Returns spread-able touch handlers.
 */
export function useLongPressMenu(onOpen: (x: number, y: number) => void, ms = 500) {
  const timerRef = React.useRef<number | null>(null)
  const startRef = React.useRef<{ x: number; y: number } | null>(null)

  const cancel = React.useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
    startRef.current = null
  }, [])

  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0]
    if (!t) return
    startRef.current = { x: t.clientX, y: t.clientY }
    timerRef.current = window.setTimeout(() => {
      const start = startRef.current
      if (start) onOpen(start.x, start.y)
    }, ms)
  }
  const onTouchMove = (e: React.TouchEvent) => {
    const t = e.touches[0]
    const start = startRef.current
    if (!t || !start) return
    // Cancel if the user starts scrolling — drift > 8px counts as a scroll.
    if (Math.abs(t.clientX - start.x) > 8 || Math.abs(t.clientY - start.y) > 8) cancel()
  }
  const onTouchEnd = () => cancel()
  const onTouchCancel = () => cancel()

  return { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel }
}
